import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { authApi, inventoryApi, projectsApi } from '../services/api'
import AdminSidebar from '../components/AdminSidebar'
import Modal from '../components/Modal'
import EmptyState from '../components/EmptyState'
import { useToast } from '../components/ToastContainer'
import {
  SearchIcon, PlusIcon, EditIcon, TrashIcon, CheckIcon, XIcon,
  ToolIcon, PackageIcon, FolderIcon, ChevronDownIcon
} from '../components/Icons'
import '../styles/EliseInventory.css'

function EliseInventory() {
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)
  const [items, setItems] = useState([])
  const [locations, setLocations] = useState([])
  const [stats, setStats] = useState(null)
  const [categories, setCategories] = useState([])
  const [projects, setProjects] = useState([])
  const navigate = useNavigate()
  const toast = useToast()

  // Filters
  const [filters, setFilters] = useState({
    item_type: '',
    category: '',
    status: '',
    location_id: '',
    search: '',
    low_stock_only: false
  })

  // View state
  const [activeTab, setActiveTab] = useState('items') // items, locations

  // Modal states
  const [showItemModal, setShowItemModal] = useState(false)
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [showUsageModal, setShowUsageModal] = useState(false)
  const [showRestockModal, setShowRestockModal] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [editingLocation, setEditingLocation] = useState(null)
  const [selectedItem, setSelectedItem] = useState(null)

  // Form states
  const [itemForm, setItemForm] = useState({
    name: '',
    item_type: 'tool',
    description: '',
    category: '',
    location_id: '',
    purchase_date: '',
    purchase_price: '',
    supplier: '',
    brand: '',
    model: '',
    serial_number: '',
    condition: 'good',
    last_maintenance_date: '',
    maintenance_notes: '',
    warranty_expiry: '',
    quantity: '',
    unit: 'ea',
    reorder_threshold: '',
    reorder_quantity: '',
    notes: '',
    tags: ''
  })

  const [locationForm, setLocationForm] = useState({
    name: '',
    description: '',
    parent_id: ''
  })

  const [usageForm, setUsageForm] = useState({
    quantity_used: '',
    usage_date: new Date().toISOString().split('T')[0],
    project_id: '',
    notes: ''
  })

  const [restockForm, setRestockForm] = useState({
    quantity: '',
    notes: ''
  })

  // Loading states
  const [saving, setSaving] = useState(false)

  // Authentication check
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        navigate('/admin/login')
        return
      }
      try {
        const response = await authApi.verify()
        setUser(response.data.user)
        loadData()
      } catch (error) {
        localStorage.removeItem('auth_token')
        navigate('/admin/login')
      }
    }
    checkAuth()
  }, [navigate])

  const loadData = async () => {
    try {
      const [itemsRes, locationsRes, statsRes, categoriesRes, projectsRes] = await Promise.all([
        inventoryApi.getAll(filters),
        inventoryApi.getLocations(),
        inventoryApi.getStats(),
        inventoryApi.getCategories(),
        projectsApi.getAll()
      ])
      setItems(itemsRes.data)
      setLocations(locationsRes.data)
      setStats(statsRes.data)
      setCategories(categoriesRes.data.categories || [])
      setProjects(projectsRes.data)
    } catch (error) {
      console.error('Error loading inventory:', error)
      toast.error('Failed to load inventory')
    } finally {
      setLoading(false)
    }
  }

  // Reload when filters change
  useEffect(() => {
    if (user) {
      loadData()
    }
  }, [filters])

  const resetItemForm = () => {
    setItemForm({
      name: '',
      item_type: 'tool',
      description: '',
      category: '',
      location_id: '',
      purchase_date: '',
      purchase_price: '',
      supplier: '',
      brand: '',
      model: '',
      serial_number: '',
      condition: 'good',
      last_maintenance_date: '',
      maintenance_notes: '',
      warranty_expiry: '',
      quantity: '',
      unit: 'ea',
      reorder_threshold: '',
      reorder_quantity: '',
      notes: '',
      tags: ''
    })
  }

  const openEditItem = (item) => {
    setEditingItem(item)
    setItemForm({
      name: item.name || '',
      item_type: item.item_type || 'tool',
      description: item.description || '',
      category: item.category || '',
      location_id: item.location_id || '',
      purchase_date: item.purchase_date ? item.purchase_date.split('T')[0] : '',
      purchase_price: item.purchase_price || '',
      supplier: item.supplier || '',
      brand: item.brand || '',
      model: item.model || '',
      serial_number: item.serial_number || '',
      condition: item.condition || 'good',
      last_maintenance_date: item.last_maintenance_date ? item.last_maintenance_date.split('T')[0] : '',
      maintenance_notes: item.maintenance_notes || '',
      warranty_expiry: item.warranty_expiry ? item.warranty_expiry.split('T')[0] : '',
      quantity: item.quantity || '',
      unit: item.unit || 'ea',
      reorder_threshold: item.reorder_threshold || '',
      reorder_quantity: item.reorder_quantity || '',
      notes: item.notes || '',
      tags: (item.tags || []).join(', ')
    })
    setShowItemModal(true)
  }

  const handleSaveItem = async (e) => {
    e.preventDefault()
    setSaving(true)

    try {
      const data = {
        ...itemForm,
        purchase_price: itemForm.purchase_price ? parseFloat(itemForm.purchase_price) : null,
        quantity: itemForm.quantity ? parseFloat(itemForm.quantity) : null,
        reorder_threshold: itemForm.reorder_threshold ? parseFloat(itemForm.reorder_threshold) : null,
        reorder_quantity: itemForm.reorder_quantity ? parseFloat(itemForm.reorder_quantity) : null,
        tags: itemForm.tags ? itemForm.tags.split(',').map(t => t.trim()).filter(t => t) : []
      }

      // Remove empty strings
      Object.keys(data).forEach(key => {
        if (data[key] === '') data[key] = null
      })

      if (editingItem) {
        await inventoryApi.update(editingItem.id, data)
        toast.success('Item updated successfully')
      } else {
        await inventoryApi.create(data)
        toast.success('Item created successfully')
      }

      setShowItemModal(false)
      setEditingItem(null)
      resetItemForm()
      loadData()
    } catch (error) {
      console.error('Error saving item:', error)
      toast.error(error.response?.data?.detail || 'Failed to save item')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return

    try {
      await inventoryApi.delete(item.id)
      toast.success('Item deleted')
      loadData()
    } catch (error) {
      console.error('Error deleting item:', error)
      toast.error(error.response?.data?.detail || 'Failed to delete item')
    }
  }

  const handleSaveLocation = async (e) => {
    e.preventDefault()
    setSaving(true)

    try {
      if (editingLocation) {
        await inventoryApi.updateLocation(editingLocation.id, locationForm)
        toast.success('Location updated')
      } else {
        await inventoryApi.createLocation(locationForm)
        toast.success('Location created')
      }

      setShowLocationModal(false)
      setEditingLocation(null)
      setLocationForm({ name: '', description: '', parent_id: '' })
      loadData()
    } catch (error) {
      console.error('Error saving location:', error)
      toast.error(error.response?.data?.detail || 'Failed to save location')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteLocation = async (location) => {
    if (!window.confirm(`Delete "${location.name}"? Items stored here will need to be reassigned.`)) return

    try {
      await inventoryApi.deleteLocation(location.id)
      toast.success('Location deleted')
      loadData()
    } catch (error) {
      console.error('Error deleting location:', error)
      toast.error(error.response?.data?.detail || 'Failed to delete location')
    }
  }

  const openUsageModal = (item) => {
    setSelectedItem(item)
    setUsageForm({
      quantity_used: '',
      usage_date: new Date().toISOString().split('T')[0],
      project_id: '',
      notes: ''
    })
    setShowUsageModal(true)
  }

  const handleLogUsage = async (e) => {
    e.preventDefault()
    setSaving(true)

    try {
      await inventoryApi.logUsage(selectedItem.id, {
        ...usageForm,
        quantity_used: parseFloat(usageForm.quantity_used)
      })
      toast.success(`Logged ${usageForm.quantity_used} ${selectedItem.unit} used`)
      setShowUsageModal(false)
      setSelectedItem(null)
      loadData()
    } catch (error) {
      console.error('Error logging usage:', error)
      toast.error(error.response?.data?.detail || 'Failed to log usage')
    } finally {
      setSaving(false)
    }
  }

  const openRestockModal = (item) => {
    setSelectedItem(item)
    setRestockForm({ quantity: '', notes: '' })
    setShowRestockModal(true)
  }

  const handleRestock = async (e) => {
    e.preventDefault()
    setSaving(true)

    try {
      await inventoryApi.restock(selectedItem.id, parseFloat(restockForm.quantity), restockForm.notes)
      toast.success(`Added ${restockForm.quantity} ${selectedItem.unit} to ${selectedItem.name}`)
      setShowRestockModal(false)
      setSelectedItem(null)
      loadData()
    } catch (error) {
      console.error('Error restocking:', error)
      toast.error(error.response?.data?.detail || 'Failed to restock')
    } finally {
      setSaving(false)
    }
  }

  const getStatusBadge = (item) => {
    if (item.item_type === 'tool') {
      const condition = item.condition || 'good'
      const colors = {
        excellent: 'badge-success',
        good: 'badge-info',
        fair: 'badge-warning',
        poor: 'badge-danger',
        needs_repair: 'badge-danger'
      }
      return <span className={`badge ${colors[condition]}`}>{condition.replace('_', ' ')}</span>
    } else {
      const status = item.status || 'in_stock'
      const colors = {
        in_stock: 'badge-success',
        low_stock: 'badge-warning',
        out_of_stock: 'badge-danger',
        on_order: 'badge-info'
      }
      return <span className={`badge ${colors[status]}`}>{status.replace('_', ' ')}</span>
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString()
  }

  const formatCurrency = (amount) => {
    if (!amount) return '-'
    return `$${parseFloat(amount).toFixed(2)}`
  }

  if (loading) return <div className="loading">Loading...</div>

  return (
    <div className="admin-panel">
      <AdminSidebar currentPage="inventory" />

      <div className="admin-content inventory-content">
        {/* Header */}
        <div className="inventory-header">
          <h1>Inventory Management</h1>
          <div className="header-actions">
            <button
              className="btn btn-primary"
              onClick={() => {
                resetItemForm()
                setEditingItem(null)
                setShowItemModal(true)
              }}
            >
              <PlusIcon size={16} /> Add Item
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon tools">
                <ToolIcon size={24} />
              </div>
              <div className="stat-info">
                <span className="stat-value">{stats.total_tools}</span>
                <span className="stat-label">Tools</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon consumables">
                <PackageIcon size={24} />
              </div>
              <div className="stat-info">
                <span className="stat-value">{stats.total_consumables}</span>
                <span className="stat-label">Consumables</span>
              </div>
            </div>
            <div className="stat-card warning">
              <div className="stat-info">
                <span className="stat-value">{stats.low_stock_items}</span>
                <span className="stat-label">Low Stock</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-info">
                <span className="stat-value">{formatCurrency(stats.total_value)}</span>
                <span className="stat-label">Total Value</span>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="inventory-tabs">
          <button
            className={`tab-btn ${activeTab === 'items' ? 'active' : ''}`}
            onClick={() => setActiveTab('items')}
          >
            Items ({items.length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'locations' ? 'active' : ''}`}
            onClick={() => setActiveTab('locations')}
          >
            Storage Locations ({locations.length})
          </button>
        </div>

        {/* Items Tab */}
        {activeTab === 'items' && (
          <>
            {/* Filters */}
            <div className="filters-bar">
              <div className="search-box">
                <SearchIcon size={18} />
                <input
                  type="text"
                  placeholder="Search items..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                />
              </div>
              <select
                value={filters.item_type}
                onChange={(e) => setFilters({ ...filters, item_type: e.target.value })}
              >
                <option value="">All Types</option>
                <option value="tool">Tools</option>
                <option value="consumable">Consumables</option>
              </select>
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <select
                value={filters.location_id}
                onChange={(e) => setFilters({ ...filters, location_id: e.target.value })}
              >
                <option value="">All Locations</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.path || loc.name}</option>
                ))}
              </select>
              <label className="checkbox-filter">
                <input
                  type="checkbox"
                  checked={filters.low_stock_only}
                  onChange={(e) => setFilters({ ...filters, low_stock_only: e.target.checked })}
                />
                Low Stock Only
              </label>
            </div>

            {/* Items List */}
            {items.length === 0 ? (
              <EmptyState
                icon="package"
                title="No Items Found"
                message={filters.search || filters.item_type || filters.category
                  ? "No items match your filters. Try adjusting your search criteria."
                  : "Start by adding your first inventory item."}
                actionLabel="Add Item"
                onAction={() => {
                  resetItemForm()
                  setEditingItem(null)
                  setShowItemModal(true)
                }}
              />
            ) : (
              <div className="items-table">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Category</th>
                      <th>Location</th>
                      <th>Status</th>
                      <th>Qty/Condition</th>
                      <th>Purchased</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => (
                      <tr key={item.id}>
                        <td>
                          <div className="item-name">
                            <strong>{item.name}</strong>
                            {item.brand && <span className="item-brand">{item.brand} {item.model}</span>}
                          </div>
                        </td>
                        <td>
                          <span className={`type-badge ${item.item_type}`}>
                            {item.item_type === 'tool' ? <ToolIcon size={14} /> : <PackageIcon size={14} />}
                            {item.item_type}
                          </span>
                        </td>
                        <td>{item.category || '-'}</td>
                        <td>{item.location_name || '-'}</td>
                        <td>{getStatusBadge(item)}</td>
                        <td>
                          {item.item_type === 'consumable'
                            ? `${item.quantity || 0} ${item.unit || ''}`
                            : (item.condition || '-')}
                        </td>
                        <td>{formatDate(item.purchase_date)}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-icon"
                              onClick={() => openEditItem(item)}
                              title="Edit"
                            >
                              <EditIcon size={16} />
                            </button>
                            {item.item_type === 'consumable' && (
                              <>
                                <button
                                  className="btn-icon use"
                                  onClick={() => openUsageModal(item)}
                                  title="Log Usage"
                                >
                                  <PackageIcon size={16} />
                                </button>
                                <button
                                  className="btn-icon restock"
                                  onClick={() => openRestockModal(item)}
                                  title="Restock"
                                >
                                  <PlusIcon size={16} />
                                </button>
                              </>
                            )}
                            <button
                              className="btn-icon delete"
                              onClick={() => handleDeleteItem(item)}
                              title="Delete"
                            >
                              <TrashIcon size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Locations Tab */}
        {activeTab === 'locations' && (
          <>
            <div className="locations-header">
              <button
                className="btn btn-primary"
                onClick={() => {
                  setEditingLocation(null)
                  setLocationForm({ name: '', description: '', parent_id: '' })
                  setShowLocationModal(true)
                }}
              >
                <PlusIcon size={16} /> Add Location
              </button>
            </div>

            {locations.length === 0 ? (
              <EmptyState
                icon="folder"
                title="No Storage Locations"
                message="Create storage locations to organize where your items are kept."
                actionLabel="Add Location"
                onAction={() => setShowLocationModal(true)}
              />
            ) : (
              <div className="locations-grid">
                {locations.map(location => (
                  <div key={location.id} className="location-card">
                    <div className="location-icon">
                      <FolderIcon size={24} />
                    </div>
                    <div className="location-info">
                      <h3>{location.name}</h3>
                      {location.path && location.path !== location.name && (
                        <span className="location-path">{location.path}</span>
                      )}
                      {location.description && (
                        <p>{location.description}</p>
                      )}
                    </div>
                    <div className="location-actions">
                      <button
                        className="btn-icon"
                        onClick={() => {
                          setEditingLocation(location)
                          setLocationForm({
                            name: location.name,
                            description: location.description || '',
                            parent_id: location.parent_id || ''
                          })
                          setShowLocationModal(true)
                        }}
                        title="Edit"
                      >
                        <EditIcon size={16} />
                      </button>
                      <button
                        className="btn-icon delete"
                        onClick={() => handleDeleteLocation(location)}
                        title="Delete"
                      >
                        <TrashIcon size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Item Modal */}
      <Modal
        isOpen={showItemModal}
        onClose={() => {
          setShowItemModal(false)
          setEditingItem(null)
        }}
        title={editingItem ? 'Edit Item' : 'Add Item'}
        size="large"
      >
        <form onSubmit={handleSaveItem}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label>Item Type *</label>
                <select
                  value={itemForm.item_type}
                  onChange={(e) => setItemForm({ ...itemForm, item_type: e.target.value })}
                  required
                >
                  <option value="tool">Tool</option>
                  <option value="consumable">Consumable</option>
                </select>
              </div>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={itemForm.name}
                  onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Category</label>
                <input
                  type="text"
                  value={itemForm.category}
                  onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}
                  list="categories-list"
                  placeholder="e.g., Hand Tools, Fasteners"
                />
                <datalist id="categories-list">
                  {categories.map(cat => (
                    <option key={cat} value={cat} />
                  ))}
                </datalist>
              </div>
              <div className="form-group">
                <label>Storage Location</label>
                <select
                  value={itemForm.location_id}
                  onChange={(e) => setItemForm({ ...itemForm, location_id: e.target.value })}
                >
                  <option value="">Select location...</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.path || loc.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={itemForm.description}
                onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                rows={2}
              />
            </div>

            {/* Tool-specific fields */}
            {itemForm.item_type === 'tool' && (
              <>
                <h4 className="form-section-title">Tool Details</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Brand</label>
                    <input
                      type="text"
                      value={itemForm.brand}
                      onChange={(e) => setItemForm({ ...itemForm, brand: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Model</label>
                    <input
                      type="text"
                      value={itemForm.model}
                      onChange={(e) => setItemForm({ ...itemForm, model: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Serial Number</label>
                    <input
                      type="text"
                      value={itemForm.serial_number}
                      onChange={(e) => setItemForm({ ...itemForm, serial_number: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Condition</label>
                    <select
                      value={itemForm.condition}
                      onChange={(e) => setItemForm({ ...itemForm, condition: e.target.value })}
                    >
                      <option value="excellent">Excellent</option>
                      <option value="good">Good</option>
                      <option value="fair">Fair</option>
                      <option value="poor">Poor</option>
                      <option value="needs_repair">Needs Repair</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Last Maintenance</label>
                    <input
                      type="date"
                      value={itemForm.last_maintenance_date}
                      onChange={(e) => setItemForm({ ...itemForm, last_maintenance_date: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Warranty Expiry</label>
                    <input
                      type="date"
                      value={itemForm.warranty_expiry}
                      onChange={(e) => setItemForm({ ...itemForm, warranty_expiry: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Maintenance Notes</label>
                  <textarea
                    value={itemForm.maintenance_notes}
                    onChange={(e) => setItemForm({ ...itemForm, maintenance_notes: e.target.value })}
                    rows={2}
                  />
                </div>
              </>
            )}

            {/* Consumable-specific fields */}
            {itemForm.item_type === 'consumable' && (
              <>
                <h4 className="form-section-title">Stock Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Current Quantity</label>
                    <input
                      type="number"
                      step="0.01"
                      value={itemForm.quantity}
                      onChange={(e) => setItemForm({ ...itemForm, quantity: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Unit</label>
                    <select
                      value={itemForm.unit}
                      onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })}
                    >
                      <option value="ea">Each (ea)</option>
                      <option value="box">Box</option>
                      <option value="pack">Pack</option>
                      <option value="ft">Feet (ft)</option>
                      <option value="in">Inches (in)</option>
                      <option value="m">Meters (m)</option>
                      <option value="lb">Pounds (lb)</option>
                      <option value="kg">Kilograms (kg)</option>
                      <option value="oz">Ounces (oz)</option>
                      <option value="gal">Gallons (gal)</option>
                      <option value="L">Liters (L)</option>
                      <option value="ml">Milliliters (ml)</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Reorder Threshold</label>
                    <input
                      type="number"
                      step="0.01"
                      value={itemForm.reorder_threshold}
                      onChange={(e) => setItemForm({ ...itemForm, reorder_threshold: e.target.value })}
                      placeholder="Alert when below this quantity"
                    />
                  </div>
                  <div className="form-group">
                    <label>Reorder Quantity</label>
                    <input
                      type="number"
                      step="0.01"
                      value={itemForm.reorder_quantity}
                      onChange={(e) => setItemForm({ ...itemForm, reorder_quantity: e.target.value })}
                      placeholder="How much to order"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Purchase Info */}
            <h4 className="form-section-title">Purchase Information</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Purchase Date</label>
                <input
                  type="date"
                  value={itemForm.purchase_date}
                  onChange={(e) => setItemForm({ ...itemForm, purchase_date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Purchase Price</label>
                <input
                  type="number"
                  step="0.01"
                  value={itemForm.purchase_price}
                  onChange={(e) => setItemForm({ ...itemForm, purchase_price: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Supplier</label>
                <input
                  type="text"
                  value={itemForm.supplier}
                  onChange={(e) => setItemForm({ ...itemForm, supplier: e.target.value })}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Tags</label>
              <input
                type="text"
                value={itemForm.tags}
                onChange={(e) => setItemForm({ ...itemForm, tags: e.target.value })}
                placeholder="Comma-separated tags"
              />
            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={itemForm.notes}
                onChange={(e) => setItemForm({ ...itemForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowItemModal(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : (editingItem ? 'Update Item' : 'Add Item')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Location Modal */}
      <Modal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        title={editingLocation ? 'Edit Location' : 'Add Location'}
      >
        <form onSubmit={handleSaveLocation}>
          <div className="modal-body">
            <div className="form-group">
              <label>Location Name *</label>
              <input
                type="text"
                value={locationForm.name}
                onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
                placeholder="e.g., Garage, Tool Chest, Drawer 1"
                required
              />
            </div>
            <div className="form-group">
              <label>Parent Location</label>
              <select
                value={locationForm.parent_id}
                onChange={(e) => setLocationForm({ ...locationForm, parent_id: e.target.value })}
              >
                <option value="">None (Top Level)</option>
                {locations
                  .filter(loc => loc.id !== editingLocation?.id)
                  .map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.path || loc.name}</option>
                  ))}
              </select>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={locationForm.description}
                onChange={(e) => setLocationForm({ ...locationForm, description: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowLocationModal(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : (editingLocation ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Usage Modal */}
      <Modal
        isOpen={showUsageModal}
        onClose={() => setShowUsageModal(false)}
        title={`Log Usage - ${selectedItem?.name}`}
      >
        <form onSubmit={handleLogUsage}>
          <div className="modal-body">
            <p className="modal-info">
              Current stock: <strong>{selectedItem?.quantity} {selectedItem?.unit}</strong>
            </p>
            <div className="form-row">
              <div className="form-group">
                <label>Quantity Used *</label>
                <input
                  type="number"
                  step="0.01"
                  value={usageForm.quantity_used}
                  onChange={(e) => setUsageForm({ ...usageForm, quantity_used: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  value={usageForm.usage_date}
                  onChange={(e) => setUsageForm({ ...usageForm, usage_date: e.target.value })}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Project (Optional)</label>
              <select
                value={usageForm.project_id}
                onChange={(e) => setUsageForm({ ...usageForm, project_id: e.target.value })}
              >
                <option value="">No project</option>
                {projects.map(proj => (
                  <option key={proj.id} value={proj.id}>{proj.title}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={usageForm.notes}
                onChange={(e) => setUsageForm({ ...usageForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowUsageModal(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Logging...' : 'Log Usage'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Restock Modal */}
      <Modal
        isOpen={showRestockModal}
        onClose={() => setShowRestockModal(false)}
        title={`Restock - ${selectedItem?.name}`}
      >
        <form onSubmit={handleRestock}>
          <div className="modal-body">
            <p className="modal-info">
              Current stock: <strong>{selectedItem?.quantity} {selectedItem?.unit}</strong>
            </p>
            <div className="form-group">
              <label>Quantity to Add *</label>
              <input
                type="number"
                step="0.01"
                value={restockForm.quantity}
                onChange={(e) => setRestockForm({ ...restockForm, quantity: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={restockForm.notes}
                onChange={(e) => setRestockForm({ ...restockForm, notes: e.target.value })}
                rows={2}
                placeholder="e.g., Order #12345, purchased from..."
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowRestockModal(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Adding...' : 'Add Stock'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

export default EliseInventory
